
import React, { useState } from 'react';
import AnalyticsDashboard from '@/components/dashboard/AnalyticsDashboard';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CalendarIcon, Download, BarChart, PieChart as PieChartIcon, LineChart } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { format, subMonths } from 'date-fns';
import { cn } from '@/lib/utils';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useIsMobile } from '@/hooks/use-mobile';

const Analytics: React.FC = () => {
  // Date range state
  const [dateRange, setDateRange] = useState<{
    from: Date;
    to: Date;
  }>({
    from: subMonths(new Date(), 6),
    to: new Date()
  });
  
  // View type state (chart view preference)
  const [viewType, setViewType] = useState<'chart' | 'pie' | 'line'>('chart');
  const isMobile = useIsMobile();

  const handleExportData = () => {
    // In a real app, this would generate and download a CSV or PDF report
    alert('Export functionality would be implemented here!');
  };

  return (
    <div className="min-h-screen bg-white py-8">
      <div className="container max-w-6xl mx-auto px-4">
        <Card className="border-front-gray-200 bg-white shadow-sm">
          <CardHeader className="pb-6">
            <div className="flex flex-col gap-6">
              <div>
                <CardTitle className="text-2xl font-bold text-front-gray-900">
                  Plant Care Analytics
                </CardTitle>
                <CardDescription className="text-front-gray-600 mt-2">
                  Track and analyze your plant care service data
                </CardDescription>
              </div>
              <div className="flex flex-wrap gap-3">
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="flex items-center gap-2 border-front-gray-300 hover:bg-front-gray-50 text-front-gray-700"
                    >
                      <CalendarIcon className="h-4 w-4" />
                      <span>
                        {format(dateRange.from, "MMM d")} - {format(dateRange.to, "MMM d")}
                      </span>
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 border-front-gray-200 bg-white" align="start">
                    <Calendar
                      initialFocus
                      mode="range"
                      selected={{
                        from: dateRange.from,
                        to: dateRange.to,
                      }}
                      onSelect={(range) => {
                        if (range?.from && range?.to) {
                          setDateRange({ from: range.from, to: range.to });
                        }
                      }}
                      numberOfMonths={isMobile ? 1 : 2}
                    />
                  </PopoverContent>
                </Popover>
                
                <Button 
                  variant="outline" 
                  className="flex items-center gap-2 border-front-gray-300 hover:bg-front-gray-50 text-front-gray-700"
                  onClick={handleExportData}
                >
                  <Download className="h-4 w-4" />
                  Export Data
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="charts" className="space-y-6">
              <TabsList className="bg-front-gray-50 border-front-gray-200">
                <TabsTrigger value="charts" className="data-[state=active]:bg-white data-[state=active]:text-front-gray-900">
                  Charts
                </TabsTrigger>
                <TabsTrigger value="summary" className="data-[state=active]:bg-white data-[state=active]:text-front-gray-900">
                  Summary
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="charts" className="space-y-6">
                <div className="flex justify-end">
                  <div className="bg-front-gray-50 inline-flex items-center rounded-lg p-1 border border-front-gray-200">
                    <Button
                      variant="ghost"
                      size="sm"
                      className={cn(
                        "rounded-md",
                        viewType === "chart" && "bg-white shadow-sm text-front-gray-900"
                      )}
                      onClick={() => setViewType("chart")}
                    >
                      <BarChart className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className={cn(
                        "rounded-md",
                        viewType === "pie" && "bg-white shadow-sm text-front-gray-900"
                      )}
                      onClick={() => setViewType("pie")}
                    >
                      <PieChartIcon className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className={cn(
                        "rounded-md",
                        viewType === "line" && "bg-white shadow-sm text-front-gray-900"
                      )}
                      onClick={() => setViewType("line")}
                    >
                      <LineChart className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <AnalyticsDashboard 
                  dateRange={dateRange}
                  viewType={viewType}
                />
              </TabsContent>
              
              <TabsContent value="summary">
                <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                  <Card className="border-front-gray-200 bg-front-gray-50">
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
                      <CardTitle className="text-sm font-semibold text-front-gray-900">Total Visits</CardTitle>
                      <BarChart className="h-4 w-4 text-front-gray-600" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold text-front-gray-900">32</div>
                      <p className="text-xs text-front-gray-600">
                        +12% from previous period
                      </p>
                    </CardContent>
                  </Card>
                  <Card className="border-front-gray-200 bg-front-gray-50">
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
                      <CardTitle className="text-sm font-semibold text-front-gray-900">Plant Health Score</CardTitle>
                      <svg className="h-4 w-4 text-front-gray-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold text-front-gray-900">94%</div>
                      <p className="text-xs text-front-gray-600">
                        +2% from previous period
                      </p>
                    </CardContent>
                  </Card>
                  <Card className="border-front-gray-200 bg-front-gray-50">
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
                      <CardTitle className="text-sm font-semibold text-front-gray-900">Avg. Service Rating</CardTitle>
                      <svg className="h-4 w-4 text-front-gray-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                      </svg>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold text-front-gray-900">4.8</div>
                      <p className="text-xs text-front-gray-600">
                        +0.2 from previous period
                      </p>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Analytics;
